import { BuyDto, Product } from "@1games-ru/region-shared/dist/backend/requests/shop";
import { PaymentType } from "@1games-ru/region-shared/dist/enums/payment/payment";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { imgShopGoods2 } from "@/shared/assets/img";
import ActionButton from "@/shared/ui/ActionButton";
import { activeKeySelector } from "@/domains/activeKey/slice";
import { playerInfoSelector } from "@/domains/player/slice";
import {
  addGoodsInCart,
  cartData,
  clearCart,
  clearSlice,
  decrementGoodsInCart,
  incrementGoodsInCart,
  isShowShop,
  removeGoodsFromCart,
  shopIdSelector,
} from "@/domains/shop/slice";
import { shopsList } from "@/domains/shop/helpers/shopsList";
import useGetProducts from "@/domains/shop/hooks/useGetProducts";
import { useNavList } from "@/domains/shop/hooks/useNavList";
import useShopHandlers from "@/domains/shop/hooks/useShopHandlers";
import useShowShop from "@/domains/shop/hooks/useShowShop";
import ShopAside from "@/domains/shop/components/ShopAside";
import ShopAsideNav from "@/domains/shop/components/ShopAsideNav";
import ShopBody from "@/domains/shop/components/ShopBody";
import ShopCartButton from "@/domains/shop/components/ShopCartButton";
import ShopCartButtons from "@/domains/shop/components/ShopCartButtons";
import ShopCartCard from "@/domains/shop/components/ShopCartCard";
import ShopCartTotal from "@/domains/shop/components/ShopCartTotal";
import ShopContentWrapper from "@/domains/shop/components/ShopContentWrapper";
import ShopGoodsCard from "@/domains/shop/components/ShopGoodsCard";
import ShopGoodsWrapper from "@/domains/shop/components/ShopGoodsWrapper";
import ShopHeader from "@/domains/shop/components/ShopHeader";
import ShopPaymentsButtons from "@/domains/shop/components/ShopPaymentsButtons";
import ShopUserBalance from "@/domains/shop/components/ShopUserBalance";
import ShopWrapper from "@/domains/shop/components/ShopWrapper";
import KeyCode from "@/assets/constants/key-code";

const EMPTY_PRODUCTS: Product[] = [];

export default function ShopContainer() {
  const dispatch = useAppDispatch();

  const cartList = useAppSelector(cartData);
  const activeKey = useAppSelector(activeKeySelector);
  const isShow = useAppSelector(isShowShop);
  const shopId = useAppSelector(shopIdSelector);
  const playerInfo = useAppSelector(playerInfoSelector);
  const cashInfo = playerInfo?.character?.money?.balance || 0;
  const cardInfo = playerInfo?.character?.money?.bank || 0;
  const characterId = playerInfo?.character?.id ?? 0;

  const { data, isLoading } = useGetProducts(isShow, shopId);
  const products = data?.products ?? EMPTY_PRODUCTS;
  const shopItem = data ? shopsList[data.type] : null;

  const [selectedGoods, setSelectedGoods] = useState<Product[]>([]);
  const baseNavList = useNavList(products);
  const [navList, setNavList] = useState(baseNavList);
  const [isCart, setIsCart] = useState(false);

  const incrementGoods = useCallback(
    (id: number) => dispatch(incrementGoodsInCart(id)),
    [dispatch],
  );
  const decrementGoods = useCallback(
    (id: number) => dispatch(decrementGoodsInCart(id)),
    [dispatch],
  );
  const removeGoods = useCallback((id: number) => dispatch(removeGoodsFromCart(id)), [dispatch]);
  const clearGoods = useCallback(() => dispatch(clearCart()), [dispatch]);
  const onExit = useCallback(() => {
    dispatch(clearSlice());
    setNavList(baseNavList);
    setSelectedGoods(products);
  }, [baseNavList, dispatch, products]);

  const color = shopItem?.color ?? null;
  const logo = shopItem?.logo ?? null;
  const header = shopItem && "header" in shopItem ? shopItem.header : "";

  useShowShop(onExit);
  const { closeShop, buyProducts, isBuying } = useShopHandlers();

  const cartCount = useMemo(() => {
    if (!cartList?.length) return { totalCount: 0, totalPrice: 0 };
    return cartList.reduce(
      (acc, item) => {
        const count = item?.count || 0;
        const price = item?.price || 0;
        return {
          totalCount: acc.totalCount + count,
          totalPrice: acc.totalPrice + count * price,
        };
      },
      { totalCount: 0, totalPrice: 0 },
    );
  }, [cartList]);

  useEffect(() => {
    setNavList((prev) => {
      if (
        prev.length === baseNavList.length &&
        prev.every(
          (v, i) =>
            v.id === baseNavList[i].id &&
            v.text === baseNavList[i].text &&
            v.isActive === baseNavList[i].isActive,
        )
      ) {
        return prev;
      }
      return baseNavList;
    });
  }, [baseNavList]);

  useEffect(() => {
    setSelectedGoods(products);
  }, [products]);

  useEffect(() => {
    if (!cartCount.totalCount) setIsCart(false);
  }, [cartCount]);

  const addToCart = useCallback(
    (product: Product) =>
      dispatch(
        addGoodsInCart({
          id: product.id,
          title: product.name,
          text: product.description,
          price: product.price,
          weight: product.weight,
          image: imgShopGoods2,
          category: product.category,
        }),
      ),
    [dispatch],
  );

  const buildBuyDto = useCallback(
    (paymentType: PaymentType): BuyDto | null => {
      if (!data || cartList.length === 0) return null;

      const buyProductsList = cartList.map((item) => ({
        id: item.id,
        amount: item.count,
      }));

      return {
        shopId: data.shopId,
        paymentType,
        products: buyProductsList,
      };
    },
    [data, cartList],
  );

  const handleBuy = useCallback(
    async (paymentType: PaymentType) => {
      const dto = buildBuyDto(paymentType);
      if (!dto || !characterId) return;
      await buyProducts(characterId, dto);
    },
    [buildBuyDto, characterId, buyProducts],
  );

  const onBuyCash = useCallback(() => handleBuy(PaymentType.Cash), [handleBuy]);
  const onBuyCard = useCallback(() => handleBuy(PaymentType.Card), [handleBuy]);

  const buySingle = useCallback(
    async (product: Product, paymentType: PaymentType) => {
      if (!data || !characterId) return;
      const dto: BuyDto = {
        shopId: data.shopId,
        paymentType,
        products: [{ id: product.id, amount: 1 }],
      };
      await buyProducts(characterId, dto);
    },
    [data, characterId, buyProducts],
  );

  const onPayCashSingle = useCallback(
    (product: Product) => buySingle(product, PaymentType.Cash),
    [buySingle],
  );
  const onPayCardSingle = useCallback(
    (product: Product) => buySingle(product, PaymentType.Card),
    [buySingle],
  );

  if (!isShow) return null;

  return (
    <AnimatePresence>
      <motion.div
        className={clsx(
          "fixed inset-y-[--boundary-offset-y] inset-x-[--boundary-offset-x] flex flex-col justify-center items-center z-40",
        )}
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{
          y: "100%",
          transition: {
            delay: 0.2,
            stiffness: 400,
          },
        }}
        transition={{ stiffness: 400 }}
      >
        <ShopWrapper>
          <ShopBody color={color}>
            <ShopHeader
              logo={logo}
              headerBg={header}
              isCart={isCart}
              setIsCart={setIsCart}
              onClose={closeShop}
            />

            <ShopContentWrapper isCart={isCart} headerBg={header}>
              <ShopAside>
                <ShopAsideNav
                  navList={navList}
                  setNavList={setNavList}
                  isCart={isCart}
                  setIsCart={setIsCart}
                  setSelectedGoods={setSelectedGoods}
                  goodsList={products}
                />
                <div className="flex flex-col gap-5">
                  <ShopUserBalance cash={cashInfo} card={cardInfo} />
                  <ShopCartButton
                    count={cartCount.totalCount}
                    isCart={isCart}
                    setIsCart={setIsCart}
                  />
                </div>
              </ShopAside>

              <ShopGoodsWrapper isLoading={isLoading} isCart={isCart}>
                {isCart ? (
                  <>
                    {cartList.map((item) => (
                      <ShopCartCard
                        key={item.id}
                        item={item}
                        cartList={cartList}
                        handleIncrement={incrementGoods}
                        handleDecrement={decrementGoods}
                        handleRemove={removeGoods}
                      />
                    ))}
                  </>
                ) : (
                  selectedGoods.map((product) => {
                    const item = {
                      id: product.id,
                      title: product.name,
                      text: product.description,
                      price: product.price,
                      weight: product.weight,
                      image: "/img/shop/goods2.png",
                      category: product.category,
                    };
                    return (
                      <ShopGoodsCard key={product.id} item={item}>
                        <ShopPaymentsButtons
                          goods={item}
                          cartList={cartList}
                          handleCart={() => addToCart(product)}
                          handleIncrement={incrementGoods}
                          handleDecrement={decrementGoods}
                          onPayCash={() => onPayCashSingle(product)}
                          onPayCard={() => onPayCardSingle(product)}
                          isPaying={isBuying}
                        />
                      </ShopGoodsCard>
                    );
                  })
                )}
              </ShopGoodsWrapper>

              {isCart && (
                <div className="absolute right-7.5 bottom-7.5 w-[calc(100%-17.5rem)]">
                  <ShopCartTotal
                    totalCount={cartCount?.totalCount}
                    totalPrice={cartCount?.totalPrice}
                  />

                  <ShopCartButtons
                    handleClear={clearGoods}
                    onBuyCash={onBuyCash}
                    onBuyCard={onBuyCard}
                    isBuying={isBuying}
                  />
                </div>
              )}
            </ShopContentWrapper>
          </ShopBody>

          <ActionButton
            action="ESC"
            label="Закрыть"
            isActive={activeKey === KeyCode.ESCAPE}
            className="min-h-10"
            color={color}
          />
        </ShopWrapper>
      </motion.div>
    </AnimatePresence>
  );
}
